import { useEffect, useMemo, useState } from "react";
import { mumsApi } from "./mumsApi";
import { MumDrawer, FormSection } from "./MumDrawer";
import { Plus, Edit3, Trash2, Loader2, CheckSquare, Sparkles, CalendarClock, Square, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["To Do", "Scheduled", "In Progress", "Completed"];
const PRIORITIES = ["High", "Medium", "Low"];
const empty = { name: "", description: "", due_date: "", priority: "Medium", garden_bed: "", related_variety: "", status: "To Do" };

export default function MumsTasks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [drawer, setDrawer] = useState(null);
  const [sugg, setSugg] = useState(null);

  const load = async () => { setLoading(true); try { setItems(await mumsApi.listTasks()); } finally { setLoading(false); } };

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [t, s] = await Promise.all([mumsApi.listTasks(), mumsApi.taskSuggestions()]);
        if (!off) { setItems(t); setSugg(s); }
      } finally { if (!off) setLoading(false); }
    })();
    return () => { off = true; };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "completed") return items.filter((t) => t.status === "Completed");
    if (filter === "active") return items.filter((t) => t.status !== "Completed");
    if (filter === "overdue") {
      const today = new Date().toISOString().slice(0, 10);
      return items.filter((t) => t.due_date && t.due_date < today && t.status !== "Completed");
    }
    return items.filter((t) => t.status === filter);
  }, [items, filter]);

  const toggleComplete = async (t) => {
    const next = t.status === "Completed" ? "To Do" : "Completed";
    await mumsApi.updateTask(t.id, { ...t, status: next });
    await load();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this task?")) return;
    await mumsApi.deleteTask(id);
    toast.success("Task removed");
    await load();
  };

  const addSeasonal = async (name) => {
    await mumsApi.createTask({ ...empty, name, status: "To Do" });
    toast.success(`"${name}" added`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="mums-tasks-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><CheckSquare size={12} /> Tasks</div>
          <h1 className="font-display text-4xl mt-2">Garden Tasks</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">A gentle to-do list for the patch.</p>
        </div>
        <button className="mum-btn" onClick={() => setDrawer("new")} data-testid="mums-task-add"><Plus size={15} /> Add task</button>
      </header>

      {/* Seasonal suggestions */}
      {sugg && (sugg.suggestions.length > 0 || sugg.seeds_to_sow_this_month.length > 0) && (
        <div className="mums-card p-4" data-testid="mums-task-suggestions">
          <h3 className="font-display text-lg text-[var(--mum-sapphire)] flex items-center gap-2"><Sparkles size={14} /> Seasonal jobs for {sugg.month}</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {sugg.suggestions.map((s) => (
              <button key={s} type="button" className="mum-btn-ghost text-xs" onClick={() => addSeasonal(s)} data-testid={`sugg-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                <Plus size={10} /> {s}
              </button>
            ))}
            {sugg.seeds_to_sow_this_month.map((s) => (
              <button key={`sow-${s}`} type="button" className="mum-btn-ghost text-xs" onClick={() => addSeasonal(`Sow ${s}`)}>
                <Plus size={10} /> Sow {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap" data-testid="mums-task-filter">
        {[{ v: "active", l: "Active" }, { v: "overdue", l: "Overdue" }, { v: "completed", l: "Completed" }, { v: "all", l: "All" }].map((b) => (
          <button key={b.v} type="button" className={`mum-tab ${filter === b.v ? "is-active" : ""}`} onClick={() => setFilter(b.v)} data-testid={`task-tab-${b.v}`}>{b.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="mums-card-quiet p-10 text-center" data-testid="mums-task-empty">
          <CalendarClock size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">No tasks {filter === "active" ? "to do — bliss." : "to show."}</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mums-task-list">
          {filtered.map((t) => {
            const today = new Date().toISOString().slice(0, 10);
            const overdue = t.due_date && t.due_date < today && t.status !== "Completed";
            return (
              <li key={t.id} className={`mums-card p-3 flex items-start gap-3 ${t.status === "Completed" ? "opacity-60" : ""} ${overdue ? "ring-1 ring-[var(--mum-cobalt)]" : ""}`} data-testid={`task-${t.id}`}>
                <button type="button" onClick={() => toggleComplete(t)} className="mt-0.5" data-testid={`task-toggle-${t.id}`}>
                  {t.status === "Completed" ? <CheckCircle2 size={20} className="text-[var(--mum-cobalt)]" /> : <Square size={20} className="text-[var(--mum-cobalt)]/60" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-display text-lg ${t.status === "Completed" ? "line-through text-[var(--mum-cobalt)]/60" : "text-[var(--mum-sapphire)]"}`}>{t.name}</h3>
                    <span className={`text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5 rounded ${t.priority === "High" ? "bg-[var(--mum-cobalt)] text-[var(--mum-ivory)]" : "bg-[var(--mum-sky)] text-[var(--mum-sapphire)]"}`}>{t.priority}</span>
                    <span className="text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--mum-silver)] text-[var(--mum-cobalt)]">{t.status}</span>
                    {overdue && <span className="text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--mum-sapphire)] text-[var(--mum-ivory)]">Overdue</span>}
                  </div>
                  {t.description && <p className="text-sm mt-1">{t.description}</p>}
                  <p className="text-xs text-[var(--mum-cobalt)]/75 mt-1">
                    {[t.due_date && `Due ${t.due_date}`, t.garden_bed && `Bed ${t.garden_bed}`, t.related_variety].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <button className="mum-btn-ghost text-xs" onClick={() => setDrawer({ edit: t })} data-testid={`task-edit-${t.id}`}><Edit3 size={12} /></button>
                  <button className="mum-btn-ghost text-xs" onClick={() => onDelete(t.id)} data-testid={`task-delete-${t.id}`}><Trash2 size={12} /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <MumDrawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer?.edit ? "Edit task" : "Add task"} testid="mums-task-drawer">
        <TaskForm initial={drawer?.edit || empty} onClose={() => setDrawer(null)} onSaved={async () => { setDrawer(null); await load(); }} />
      </MumDrawer>
    </div>
  );
}

function TaskForm({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [form, setForm] = useState({ ...empty, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Task name required"); return; }
    setBusy(true);
    try {
      if (editing) await mumsApi.updateTask(initial.id, form);
      else await mumsApi.createTask(form);
      toast.success(editing ? "Task updated" : "Task added");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <FormSection title="The Task">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Name *</span>
          <input className="mum-field mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="task-name" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Description</span>
          <textarea className="mum-field mt-1.5" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Due date</span>
            <input type="date" className="mum-field mt-1.5" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Status</span>
            <select className="mum-field mt-1.5" value={form.status} onChange={(e) => set("status", e.target.value)}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Priority</span>
            <select className="mum-field mt-1.5" value={form.priority} onChange={(e) => set("priority", e.target.value)}>
              {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Bed</span>
            <input className="mum-field mt-1.5" value={form.garden_bed} onChange={(e) => set("garden_bed", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Variety</span>
            <input className="mum-field mt-1.5" value={form.related_variety} onChange={(e) => set("related_variety", e.target.value)} />
          </label>
        </div>
      </FormSection>
      <div className="flex gap-2">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1" disabled={busy}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {editing ? "Save" : "Add task"}
        </button>
      </div>
    </form>
  );
}
